/**
 * The refusals the Harness endpoints can answer with, one class each.
 *
 * Mirrors `llm-profiles/errors.ts`: each refusal is a different thing for the
 * person to do, so a typed class per refusal rather than a bare `code` string
 * a component would have to narrow itself (constitution §2.5 forbids
 * narrowing `unknown` without a type guard).
 */

export type HarnessErrorField =
  'name' | 'harness_type' | 'base_url' | 'token' | 'form';

class HarnessError extends Error {
  readonly status: number;
  readonly field: HarnessErrorField;

  constructor(
    name: string,
    status: number,
    field: HarnessErrorField,
    message: string,
  ) {
    super(message);
    this.name = name;
    this.field = field;
    this.status = status;
  }
}

/** The name is empty after trimming, or over length. */
export class HarnessNameInvalidError extends HarnessError {
  constructor(message?: string) {
    super('HarnessNameInvalidError', 422, 'name', message ?? 'Enter a name.');
  }
}

/**
 * `harness_type` is not one this deployment offers (spec 240 FR-014). The
 * service words the refusal itself, naming the offered types, so that sentence
 * is shown as is — the UI has no list of types to rebuild it from.
 */
export class HarnessTypeNotOfferedError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessTypeNotOfferedError',
      422,
      'harness_type',
      message ?? 'That harness type is not offered by this deployment.',
    );
  }
}

/** Another harness already has this name (two admins creating it at once). */
export class HarnessNameConflictError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessNameConflictError',
      409,
      'name',
      message ?? 'A harness with this name already exists. Choose another.',
    );
  }
}

/** `base_url` is non-blank but not a well-formed URL. */
export class HarnessBaseUrlInvalidError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessBaseUrlInvalidError',
      422,
      'base_url',
      message ?? 'Enter a well-formed URL, or leave it blank.',
    );
  }
}

/** A token was required (create) but missing or blank. */
export class HarnessTokenRequiredError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessTokenRequiredError',
      422,
      'token',
      message ?? 'Enter a token.',
    );
  }
}

/** A wire API was sent for a harness type it does not apply to. */
export class HarnessWireApiInapplicableError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessWireApiInapplicableError',
      422,
      'form',
      message ?? 'Wire API only applies to OpenAI harnesses.',
    );
  }
}

/** The wire API is not one of the supported values. */
export class HarnessWireApiInvalidError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessWireApiInvalidError',
      422,
      'form',
      message ?? 'Choose Responses API or Chat Completions.',
    );
  }
}

/** The harness is gone — deleted from another tab, or by someone else. */
export class HarnessNotFoundError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessNotFoundError',
      404,
      'form',
      message ?? 'That Harness no longer exists. The list has been refreshed.',
    );
  }
}

/**
 * At least one LLM Profile still references this harness (spec FR-009).
 *
 * Form-level and named explicitly — the admin needs to know *why* the delete
 * was refused, not just that it was.
 */
export class HarnessHasDependentProfilesError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessHasDependentProfilesError',
      409,
      'form',
      message ??
        'This harness is still referenced by at least one LLM Profile, so it cannot be deleted. Reassign or delete those profiles first.',
    );
  }
}

/** This harness is the current default (spec FR-009b). */
export class HarnessIsDefaultError extends HarnessError {
  constructor(message?: string) {
    super(
      'HarnessIsDefaultError',
      409,
      'form',
      message ??
        'This is the default Harness, so it cannot be deleted. Set a different harness as default first.',
    );
  }
}

/** A refusal this build does not recognise. Form-level and deliberately vague. */
export class HarnessRequestFailedError extends HarnessError {
  constructor(status: number, message?: string) {
    super(
      'HarnessRequestFailedError',
      status,
      'form',
      message ?? 'The request failed. Please try again.',
    );
  }
}

/** The codes the harness routes answer with (product-x-next-service `ai_providers/errors.py`). */
const BY_CODE: Record<string, (message?: string) => HarnessError> = {
  AI_PROVIDER_NAME_INVALID: (m) => new HarnessNameInvalidError(m),
  AI_PROVIDER_TYPE_UNSUPPORTED: (m) => new HarnessTypeNotOfferedError(m),
  AI_PROVIDER_BASE_URL_INVALID: (m) => new HarnessBaseUrlInvalidError(m),
  AI_PROVIDER_TOKEN_REQUIRED: (m) => new HarnessTokenRequiredError(m),
  AI_PROVIDER_WIRE_API_INAPPLICABLE: (m) =>
    new HarnessWireApiInapplicableError(m),
  AI_PROVIDER_WIRE_API_INVALID: (m) => new HarnessWireApiInvalidError(m),
  HARNESS_NAME_CONFLICT: (m) => new HarnessNameConflictError(m),
  AI_PROVIDER_NOT_FOUND: (m) => new HarnessNotFoundError(m),
  AI_PROVIDER_IN_USE: (m) => new HarnessHasDependentProfilesError(m),
  AI_PROVIDER_IS_DEFAULT: (m) => new HarnessIsDefaultError(m),
};

/**
 * Map one error response to its typed refusal.
 *
 * The service's own `message` is passed through only for the type refusal, whose
 * whole point is the list of offered types it names; every other refusal keeps
 * the UI's own wording (see `llm-profiles/errors.ts`'s identical note). An
 * unrecognised code produces {@link HarnessRequestFailedError} instead of throwing.
 */
export function toHarnessError(status: number, body: unknown): HarnessError {
  const record =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)
      : undefined;
  const code = record?.error ?? record?.code;
  const message =
    typeof record?.message === 'string' ? record.message : undefined;

  if (code === 'AI_PROVIDER_TYPE_UNSUPPORTED') {
    return new HarnessTypeNotOfferedError(message);
  }
  const construct = typeof code === 'string' ? BY_CODE[code] : undefined;
  return construct ? construct() : new HarnessRequestFailedError(status);
}

/** Narrows a mutation's `unknown` error to a typed refusal (constitution §2.5). */
export function isHarnessError(error: unknown): error is HarnessError {
  return error instanceof HarnessError;
}

export type { HarnessError };
